import { formatMoney } from '@zlinkx/shared-v2';
import type { TranslationFunction } from '@/i18n';
import { Numeric } from '@/utils/numeric';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getNestedValue(source: unknown, path: string[]): unknown {
  let current: unknown = source;

  for (const segment of path) {
    if (!isRecord(current) || !(segment in current)) {
      return undefined;
    }
    current = current[segment];
  }

  return current;
}

function getNestedString(source: unknown, path: string[]): string | undefined {
  const value = getNestedValue(source, path);
  return typeof value === 'string' ? value : undefined;
}

/**
 * Formats error messages containing cent values to display as dollars
 * Handles both English and Chinese number formats
 *
 * @param message - The error message potentially containing cent values
 * @returns The formatted message with dollar amounts
 */
export function formatCurrencyInErrorMessage(message: string): string {
  if (message.length === 0) return message;

  // Pattern to match various cent formats:
  // - "10000 cents"
  // - "10,000 cents"
  // - "10000cents" (no space)
  // - Case insensitive
  const centPattern = /(\d+(?:,\d{3})*)\s*cents?\b/gi;

  return message.replace(centPattern, (match: string, cents: string): string => {
    // Remove commas for parsing
    const cleanCents = String(cents).replace(/,/g, '');
    const centValue = Numeric.parseInt(cleanCents, Number.NaN);

    if (Number.isNaN(centValue)) {
      return match; // Return original if parsing fails
    }

    // Format as money (dollars)
    return formatMoney(centValue);
  });
}

/**
 * Formats API error messages for user display
 * Handles both direct string messages and structured error objects
 *
 * @param error - The error object from API
 * @param t - Optional translation function for i18n support
 * @returns User-friendly error message
 */
export function formatApiErrorMessage(
  error: unknown,
  t?: TranslationFunction
): string {
  // Extract the error message
  const message =
    typeof error === 'string'
      ? error
      : getNestedString(error, ['message']) ??
        getNestedString(error, ['error', 'message']) ??
        getNestedString(error, ['response', 'data', 'error', 'message']) ??
        '';

  // If no message found, return a generic error
  if (message.length === 0) {
    return t !== undefined ? t('errors.unexpectedError') : 'An unexpected error occurred';
  }

  // Format currency values in the message
  const formattedMessage = formatCurrencyInErrorMessage(message);

  // Handle common error patterns with translations
  if (t !== undefined) {
    // Check for minimum order amount errors
    const minimumOrderMatch = formattedMessage.match(/minimum\s+order\s+amount\s+is\s+([$\d.]+)/i);
    if (minimumOrderMatch !== null) {
      return t('errors.minimumOrderAmount', { amount: minimumOrderMatch[1] ?? '' });
    }

    const lowerMessage = formattedMessage.toLowerCase();

    // Check for insufficient funds
    if (lowerMessage.includes('insufficient funds')) {
      return t('errors.insufficientFunds');
    }

    // Check for coupon already used
    if (lowerMessage.includes('already used') || lowerMessage.includes('already redeemed')) {
      return t('errors.couponAlreadyUsed');
    }

    // Check for coupon expired
    if (lowerMessage.includes('expired')) {
      return t('errors.couponExpired');
    }

    // Check for invalid coupon
    if (lowerMessage.includes('invalid coupon') || lowerMessage.includes('coupon not found')) {
      return t('errors.invalidCoupon');
    }
  }

  // Return the formatted message as-is if no specific translation applies
  return formattedMessage;
}

/**
 * Extracts and formats validation errors from API responses
 *
 * @param error - The error object from API
 * @param t - Optional translation function
 * @returns Array of formatted validation error messages
 */
export function formatValidationErrors(
  error: unknown,
  t?: TranslationFunction
): string[] {
  const errors: string[] = [];

  // Check for validation errors in various formats
  const validationErrors =
    getNestedValue(error, ['validationErrors']) ??
    getNestedValue(error, ['errors']) ??
    getNestedValue(error, ['response', 'data', 'errors']) ??
    getNestedValue(error, ['response', 'data', 'validationErrors']);

  if (Array.isArray(validationErrors)) {
    validationErrors.forEach(err => {
      if (typeof err === 'string') {
        errors.push(formatCurrencyInErrorMessage(err));
        return;
      }

      if (!isRecord(err)) {
        return;
      }

      const nestedMessage =
        (typeof err.message === 'string' ? err.message : undefined) ??
        (typeof err.msg === 'string' ? err.msg : undefined);

      if (nestedMessage !== undefined) {
        errors.push(formatCurrencyInErrorMessage(nestedMessage));
      }
    });
  } else if (isRecord(validationErrors)) {
    // Handle object-based validation errors (field: message)
    Object.values(validationErrors).forEach(err => {
      if (typeof err === 'string') {
        errors.push(formatCurrencyInErrorMessage(err));
      } else if (Array.isArray(err)) {
        err.forEach(e => {
          if (typeof e === 'string') {
            errors.push(formatCurrencyInErrorMessage(e));
          }
        });
      }
    });
  }

  // If no specific validation errors found, try to format the main error
  if (errors.length === 0 && error !== null && error !== undefined) {
    const mainError = formatApiErrorMessage(error, t);
    if (mainError.length > 0) {
      errors.push(mainError);
    }
  }

  return errors;
}

/**
 * Determines if an error should be shown to the user
 * Some errors might be handled silently or logged only
 *
 * @param error - The error object
 * @returns Whether to show the error to the user
 */
export function shouldShowErrorToUser(error: unknown): boolean {
  // Don't show network cancellation errors
  const errorCode = getNestedString(error, ['code']);
  const errorMessage = getNestedString(error, ['message']);

  if (errorCode === 'ECONNABORTED' || (errorMessage !== undefined && errorMessage.includes('cancelled'))) {
    return false;
  }

  // Don't show 401 errors (handled by auth system)
  const status = getNestedValue(error, ['status']);
  const responseStatus = getNestedValue(error, ['response', 'status']);
  if (status === 401 || responseStatus === 401) {
    return false;
  }

  // Show all other errors
  return true;
}
